import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { useLocale } from '../i18n/LocaleContext';

const CATEGORIES = [
  { value: 'credits', labelKey: 'support.credits' },
  { value: 'billing', labelKey: 'support.billing' },
  { value: 'technical', labelKey: 'support.technical' },
  { value: 'other', labelKey: 'support.other' }
];

function SupportCenter() {
  const { t } = useLocale();
  const [question, setQuestion] = useState('');
  const [qaHistory, setQaHistory] = useState([]);
  const [asking, setAsking] = useState(false);
  const [qaError, setQaError] = useState('');

  const [ticketForm, setTicketForm] = useState({ category: 'other', subject: '', description: '' });
  const [tickets, setTickets] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [ticketError, setTicketError] = useState('');

  useEffect(() => {
    api.myTickets().then(res => setTickets(res.tickets)).catch(() => {});
  }, []);

  const handleAsk = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    setQaError('');
    setAsking(true);
    const askedQuestion = question;
    setQuestion('');
    try {
      const res = await api.askHelpCenter(askedQuestion);
      setQaHistory(h => [...h, { question: askedQuestion, answer: res.answer }]);
    } catch (err) {
      setQaError(err.response?.data?.error || err.message);
    } finally {
      setAsking(false);
    }
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    setTicketError('');
    setSubmitting(true);
    try {
      const res = await api.submitTicket(ticketForm.category, ticketForm.subject, ticketForm.description);
      setTickets(t => [res.ticket, ...t]);
      setTicketForm({ category: 'other', subject: '', description: '' });
    } catch (err) {
      setTicketError(err.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="support-center">
      <div className="section-heading">{t('support.title')}</div>
      <p style={{ color: 'var(--text-secondary)', fontSize: '12.5px', marginBottom: '1rem' }}>
        {t('support.description')}
      </p>
      <div className="chat-window" style={{ minHeight: '120px', marginBottom: '1rem' }}>
        {qaHistory.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>{t('support.emptyQuestion')}</div>}
        {qaHistory.map((qa, i) => (
          <div key={i} style={{ marginBottom: '0.75rem' }}>
            <div className="chat-bubble user" style={{ marginBottom: '0.3rem' }}><div className="chat-bubble-label">{t('support.you')}</div>{qa.question}</div>
            <div className="chat-bubble agent"><div className="chat-bubble-label">{t('support.guide')}</div>{qa.answer}</div>
          </div>
        ))}
      </div>
      {qaError && <div className="auth-error" style={{ marginBottom: '0.75rem' }}>{qaError}</div>}
      <form onSubmit={handleAsk} className="chat-input-row" style={{ marginBottom: '2.5rem' }}>
        <input type="text" value={question} onChange={e => setQuestion(e.target.value)} placeholder={t('support.questionPlaceholder')} />
        <button type="submit" disabled={asking}>{asking ? '...' : t('support.ask')}</button>
      </form>

      <div className="section-heading">{t('support.ticketTitle')}</div>
      <p style={{ color: 'var(--text-secondary)', fontSize: '12.5px', marginBottom: '1rem' }}>
        {t('support.ticketDescription')}
      </p>
      <form onSubmit={handleSubmitTicket} className="builder-form" style={{ marginBottom: '1.5rem' }}>
        <div className="form-group">
          <label>{t('support.category')}</label>
          <select value={ticketForm.category} onChange={e => setTicketForm({ ...ticketForm, category: e.target.value })}>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{t(c.labelKey)}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>{t('support.subject')}</label>
          <input type="text" value={ticketForm.subject} onChange={e => setTicketForm({ ...ticketForm, subject: e.target.value })} required />
        </div>
        <div className="form-group">
          <label>{t('support.descriptionLabel')}</label>
          <textarea rows={4} value={ticketForm.description} onChange={e => setTicketForm({ ...ticketForm, description: e.target.value })} required />
        </div>
        {ticketError && <div className="auth-error">{ticketError}</div>}
        <button type="submit" className="submit-btn" disabled={submitting}>{submitting ? t('support.submitting') : t('support.submit')}</button>
      </form>

      {tickets.length > 0 && (
        <>
          <div className="section-heading">{t('support.tickets')}</div>
          <div className="activity-list">
            {tickets.map(ticket => (
              <div key={ticket.id} style={{ padding: '0.75rem 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: 600 }}>
                  <span>{ticket.subject}</span>
                  <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>{ticket.category} \u2022 {ticket.status}</span>
                </div>
                <div style={{ fontSize: '12.5px', color: 'var(--text-secondary)', marginTop: '0.3rem' }}>{ticket.aiDraftResponse}</div>
                {(ticket.updates || []).filter(update => update.actor === 'support').map(update => (
                  <div key={update.id} style={{ marginTop: '0.55rem', paddingTop: '0.55rem', borderTop: '1px solid var(--border)', fontSize: '12.5px' }}>
                    <strong>{t('support.supportTeam')} · {update.status.replace(/_/g, ' ')}</strong>
                    <div style={{ color: 'var(--text-secondary)', marginTop: '0.2rem' }}>{update.message}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default SupportCenter;
